// Farbtöne der Immobilien-Ansicht im Risiken-Reiter.
//
// WARUM EIGENE TOKENS: Der übrige Reiter läuft in Aubergine/Rosé (`BRAND`,
// `REFINED`). Die Immobilienansicht ist eine ANDERE Gliederung derselben
// Seite — Gebäude statt Vermögenspositionen — und trägt deshalb die blaue
// Variante, die es im Produkt schon gibt: `blueTheme` aus der großen
// Schadenmeldung (`components/schadenmeldung/theme.ts`). Dieselben Werte, nur
// in der Form, die die Fluent-`makeStyles`-Blöcke hier brauchen.
//
// Die Schlüssel heißen wie in `BRAND`, damit ein Style-Block ohne Umbenennen
// zwischen beiden Paletten wechseln kann.

import type { CSSProperties } from "react";
import { blueTheme } from "../schadenmeldung/theme";
import { BRAND } from "../risikoanalyseStep/tokens";

export const IMMO = {
  fontPrimary: BRAND.fontPrimary,
  fontAccent: BRAND.fontAccent,

  /** Dunkelblau — Überschriften und Werte (ersetzt BRAND.ink). */
  ink: blueTheme.ink, // #0a1a2e
  /** Akzent — Nummern, aktive Ränder, Links (ersetzt BRAND.purple). */
  blau: blueTheme.primary, // #0b2645
  /** Tiefes Blau für Flächen mit heller Schrift. */
  blauTief: blueTheme.primaryDeep, // #07182d
  /** Gold bleibt Gold: die CI-Akzentfarbe ist in beiden Themes dieselbe. */
  gold: blueTheme.accent, // #f7a823
  white: "#ffffff",

  /** Kartengrund — kühles Weißblau statt des rosé Tons. */
  cardBg: blueTheme.cardBg, // #f6f9fd
  /** Rahmen und Trennlinien. */
  border: blueTheme.cardBorder, // #D6E1EE
  /** Zarter Rahmen für gestapelte Zeilen. */
  borderDashed: "#E6EEF6",
  /** Sekundärtext. */
  muted: "#5b7288",
  /** Sehr helle Fläche — Hover, Tabellenkopf, Detailbereich. */
  bgSoft: "#eef4fb",
  /** Etwas kräftiger — ausgewählte Zeile. */
  bgSelect: "#e2ecf8",

  /** Statusfarben — bewusst NICHT blau, sie müssen sich abheben. */
  okBg: "#e6f3ec",
  okInk: "#1e6b3a",
  warnBg: "#fdeeea",
  warnInk: "#a3402b",
} as const;

/**
 * Überschreibt die `.kvp-rd`-Variablen (Aubergine) mit der blauen Skala — für
 * Bausteine, die ihre Farben aus dem CSS beziehen statt aus `makeStyles`,
 * allen voran das Organigramm-SVG. Als Inline-Style auf den Wrapper gesetzt,
 * damit die Umfärbung genau so weit reicht wie die Immobilienansicht.
 */
export const IMMO_CSS_VARS: CSSProperties = {
  /* Abschnitte des Risiken-Reiters (siehe `V` in ContactRisksPanel). */
  ["--risk-ink" as string]: IMMO.ink,
  ["--risk-akzent" as string]: IMMO.blau,
  ["--risk-gold" as string]: IMMO.blau,
  ["--risk-muted" as string]: IMMO.muted,
  ["--risk-linie" as string]: IMMO.border,
  ["--risk-chip-bg" as string]: IMMO.bgSelect,
  ["--risk-chip-on" as string]: IMMO.bgSoft,
  ["--risk-rule" as string]: `linear-gradient(90deg, ${IMMO.blau} 0%, rgba(11,38,69,0) 100%)`,

  ["--aub" as string]: IMMO.blau,
  ["--aub-600" as string]: "#123a63",
  ["--aub-700" as string]: IMMO.blauTief,
  ["--aub-50" as string]: IMMO.bgSoft,
  ["--aub-100" as string]: IMMO.bgSelect,
  ["--aub-200" as string]: "#bcd3ea",
  ["--aub-300" as string]: "#8fb3d4",
  ["--aub-deep" as string]: IMMO.blauTief,
  ["--mag" as string]: "#1f6fb2",
  ["--ink" as string]: IMMO.ink,
  ["--ink-2" as string]: IMMO.muted,
  ["--ink-3" as string]: "#8fa4b8",
  ["--line" as string]: IMMO.borderDashed,
  ["--line-2" as string]: IMMO.border,
  ["--field-2" as string]: IMMO.cardBg,
  /** Eigene Variablen des Organigramms — siehe contactRedesign.css. */
  ["--org-marke-bg" as string]: IMMO.bgSoft,
  ["--org-marke-line" as string]: IMMO.border,
  ["--org-marke-ink" as string]: IMMO.blau,
} as CSSProperties;
